import { BASE_API_URLS } from "../utils/utils";

const STANDARD_GALLERY_URL = "/api/v1/embed/:id";
const PRODUCT_GALLERY_URL = "/api/v1/shop/:shop/product/:handle";

export class GalleryLoaderService {
  public data;
  public shopId;
  public embedId;
  public galleryId;
  public productHandle;
  public shopDomain;
  public page = 0;
  public isLoading = false;
  public totalPages = 1;
  public previewSettings = {};
  public previewHoverMode: boolean = false;
  public previewPopupMode: boolean = false;
  public previewMode: boolean = false;
  public sortType: string = "";

  private _environment: string;
  private _customSettings: object = {};
  private _selectedFilters: Array<string> = [];

  constructor(env: string, previewMode?: boolean) {
    this._environment = "production";

    if (typeof previewMode === "boolean") { this.previewMode = previewMode; }

    if (typeof env === "string" && Object.keys(BASE_API_URLS).includes(env)) {
      this._environment = env;
    }
  }

  get baseUrl() {
    return BASE_API_URLS[this._environment];
  }

  // returns `standard_gallery` or `product_gallery`
  get galleryType() {
    return this.productHandle ? "product_gallery" : "standard_gallery";
  }

  setCustomSettings(settings: object): object {
    this._customSettings = { ...settings };
    Object.keys(this._customSettings).forEach(key =>
      this._customSettings[key] === null
        ? delete this._customSettings[key]
        : this._customSettings[key]);
    return this._customSettings;
  }

  setSelectedFilters(filters: Array<string>): Array<string> {
    return (this._selectedFilters = [...filters]);
  }

  standardGalleryUrl() {
    return STANDARD_GALLERY_URL.replace(":id", this.embedId);
  }

  productGalleryUrl() {
    return PRODUCT_GALLERY_URL.replace(":shop", this.shopDomain).replace(":handle", this.productHandle);
  }

  galleryUrl() {
    if (this._environment === "testing") return "http://localhost:3000/embed";

    let url = this.baseUrl;
    this.productHandle
      ? url += this.productGalleryUrl()
      : url += this.standardGalleryUrl();

    if (this.previewMode) url += "/preview"; 
    if (this.sortType !== "") url += `?sort_by=${this.sortType}`;
    return url;
  }

  resetPagination() {
    this.page = 0;
    this.totalPages = 1;
  }

  async fetchItems(): Promise<any> {
    if (this.isLoading || this.page >= this.totalPages) { return; }

    this.isLoading = true;
    this.page += 1;

    let url = this.galleryUrl();
    const queryFragments = [];

    if (this.page > 1) { queryFragments.push(`page=${ this.page }`); }

    if (this._selectedFilters.length > 0) {
      if (this.previewMode) {
        this.previewSettings["filters"] = this._selectedFilters;
      } else {
        this._selectedFilters.forEach(f => queryFragments.push(encodeURIComponent("filters[]") + "=" + encodeURIComponent(f)));
      }
    }

    if (queryFragments.length > 0) { url += `?${ queryFragments.join("&") }`; }

    const response = await fetch(url, this.fetchOptions);

    if (!response.ok) {
      throw new Error(`${response.status}`);
    }

    const data = await response.json();
    data.items = this.getItemsWithLocaleLinks(data.items);  
    this.data = { ...data };
    this.data.settings = { ...this.data.settings, ...this._customSettings };
    data.settings = { ...data.settings, ...this._customSettings };
    this.totalPages = data.total_pages;
    this.galleryId = data.gallery_id;
    this.shopId = data.shop_id;

    this.isLoading = false;
    return data;
  }

  private getItemsWithLocaleLinks(items) {
    const getLocaleLink = (url: string): string => {
      const hostname = window.location.hostname; // TODO 1 enable real Shopify root
      let localeUrl = url;
      const shopify = (window as any).Shopify; // TODO 2 enable real Shopify root
      if (typeof shopify !== "undefined" && shopify.routes && shopify.routes.root && shopify.routes.root !== "/") {
        return localeUrl.replace(`${hostname}/`, `${hostname}${shopify.routes.root}`);
      }
      return localeUrl;
    };
  
    items.forEach(item => {
      if (item.links && item.links.length > 0) {
        item.links.forEach(link => {
          link.link = getLocaleLink(link.link);
          return link;
        });
      }
      return item;
    });
  
    return items;
  }

  private get fetchOptions(): object {
    const formattedPayload = {};
    const _fetchOptions = {};
    const keys = Object.keys(this.previewSettings);

    if (this.previewMode) {
      _fetchOptions["method"] = "POST";
      _fetchOptions["headers"] = { "Content-Type": "application/json" };

      if (keys.length > 0) {
        keys.forEach(key => {
          const strippedKey = key.replace(/^(gallery_embed\[)/, "").replace(/\]$/, "");
          formattedPayload[strippedKey] = this.previewSettings[key];
        });
        _fetchOptions["body"] = JSON.stringify(formattedPayload);
      }
    }

    return _fetchOptions;
  }
}
